import React, { useState, useEffect } from "react";
import toastr from "toastr";
import { authFetch } from "../utils/authFetch";

/* ───── current user from localStorage ───── */
const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
const userId = currentUser.id || "";       // e.g. "685cdc2dad660baaacc2b099"

/* ───── toast helper ───── */
const showToast = (message, type = "success") => {
  toastr.options = {
    closeButton: true,
    progressBar: true,
    positionClass: "toast-top-right",
    timeOut: "3000",
  };
  toastr[type](message);
};

function Bank() {
  /* ───── initial (reset‑safe) form state ───── */
  const initialForm = {
    name: "",
    account: "",
    ifsc: "",
    branch: "",
    user_id: userId,
    status: "active",
  };

  const [bankForm, setBankForm] = useState(initialForm);
  const [banks, setBanks] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ───── list api ───── */
  const fetchBanks = async () => {
    try {
      // const res = await fetch(
      //   // backend should accept ?user_id= filter
      //   `http://127.0.0.1:6060/api/v1/bank/list?user_id=${userId}`,
      //   { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
      // );
      const res = await authFetch(`/api/v1/bank/list?user_id=${userId}`);
      if (!res) return;
      const data = await res.json();
      setBanks(Array.isArray(data) ? data : []);
    } catch {
      showToast("Failed to fetch banks", "error");
    }
  };

  useEffect(() => {
    fetchBanks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ───── create / update ───── */
  const handleCreateOrUpdate = async () => {
    if (!bankForm.name || !bankForm.account || !bankForm.ifsc) {
      showToast("Name, Account and IFSC are required", "error");
      return;
    }

    setLoading(true);
    const url = editingId
      ? "/api/v1/bank/update"
      : "/api/v1/bank/create";
    const method = editingId ? "PUT" : "POST";

    try {
      const res = await authFetch(url, {
        method,
        // headers: {
        //   "Content-Type": "application/json",
        //   Authorization: `Bearer ${localStorage.getItem("token")}`,
        // },
        // user_id hard‑coded as safety net
        body: JSON.stringify({ ...bankForm, user_id: userId, id: editingId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong");

      showToast(editingId ? "Bank updated" : "Bank created", editingId ? "info" : "success");

      setBankForm(initialForm);    // preserve user_id
      setEditingId(null);
      fetchBanks();
      document.querySelector("#bankModal .btn-close")?.click();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  /* ───── edit ───── */
  const handleEdit = (bank) => {
    setBankForm({
      name: bank.name || "",
      account: bank.account || "",
      ifsc: bank.ifsc || "",
      branch: bank.branch || "",
      user_id: bank.user_id || userId,
      status: bank.status || "active",
    });
    setEditingId(bank.id);
  };

  /* ───── delete ───── */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this bank?")) return;
    try {
      // const res = await fetch(`http://127.0.0.1:6060/api/v1/bank/delete/${id}`, {
      //   method: "DELETE",
      //   headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      // });
      const res = await authFetch(`/api/v1/bank/delete/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Delete failed");
      showToast("Bank deleted", "error");
      fetchBanks();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const handleChange = (e) =>
    setBankForm({ ...bankForm, [e.target.name]: e.target.value });

  return (
    <div className="page-wrapper">
      <div className="page-content-wrapper">
        <div className="page-content">
          <div className="card">
            <div className="card-body">
              <div className="card-title d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Bank Manager</h4>
                <button
                  className="btn btn-primary d-none"
                  data-bs-toggle="modal"
                  data-bs-target="#bankModal"
                  onClick={() => {
                    setBankForm({
                      name: "",
                      account: "",
                      ifsc: "",
                      branch: "",
                      user_id: "",
                      status: "active",
                    });
                    setEditingId(null);
                  }}
                >
                  Add
                </button>
              </div>

              {/* Modal */}
              <div className="modal fade" id="bankModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">{editingId ? "Edit Bank" : "Add Bank"}</h5>
                      <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <label className="form-label">Bank Name</label>
                        <input name="name" placeholder="Bank Name" className="form-control mb-2" value={bankForm.name} onChange={handleChange} />

                        <label className="form-label">Account Number</label>
                        <input name="account" placeholder="Account Number" className="form-control mb-2" value={bankForm.account} onChange={handleChange} />

                        <label className="form-label">IFSC Code</label>
                        <input name="ifsc" placeholder="IFSC Code" className="form-control mb-2" value={bankForm.ifsc} onChange={handleChange} />

                        <label className="form-label">Branch</label>
                        <input name="branch" placeholder="Branch" className="form-control mb-2" value={bankForm.branch} onChange={handleChange} />

                        {/* <label className="form-label">User ID</label>
                        <input name="user_id" placeholder="User ID" className="form-control mb-2" value={bankForm.user_id} onChange={handleChange} /> */}

                        <label className="form-label">Status</label>
                        <select name="status" className="form-control mb-2" value={bankForm.status} onChange={handleChange}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                      <button className="btn btn-primary" onClick={handleCreateOrUpdate} disabled={loading}>
                        {loading ? "Saving..." : editingId ? "Update" : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <hr />
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Account</th>
                      <th>IFSC</th>
                      <th>Branch</th>
                      {/* <th>User ID</th> */}
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {banks.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center">No Banks found</td>
                      </tr>
                    )}
                    {banks.map((bank) => (
                      <tr key={bank.id}>
                        <td>{bank.name}</td>
                        <td>{bank.account}</td>
                        <td>{bank.ifsc}</td>
                        <td>{bank.branch}</td>
                        {/* <td>{bank.user_id}</td> */}
                        <td>{bank.status}</td>
                        <td>
                          <button className="btn btn-sm btn-info me-2" data-bs-toggle="modal" data-bs-target="#bankModal" onClick={() => handleEdit(bank)}>
                            <span class="bx bx-edit"></span>
                          </button>
                          <button className="btn btn-sm btn-danger d-none" onClick={() => handleDelete(bank.id)}>
                            <span class="bx bx-trash"></span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Bank;
